import React from 'react';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations'; // ADD THIS IMPORT
import '../../../styles/OrganizationSelector.css';

interface Organization {
  id: string;
  name: string;
  locationName: string; // Add location info
  permissions: string[]; // e.g., ['read', 'write', 'admin']
}

interface OrganizationSelectorProps {
  onSelectOrganization: (organizationId: string) => void;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ 
  onSelectOrganization 
}) => {
  const { userPermissions } = useAuth();
  const { getOrganizationName, getOrganizationById, loading: locationsLoading } = useLocations(); // ADD THIS LINE

  // Helper function to format organization names nicely
  const formatOrganizationName = (name: string): string => {
    // Convert from "DINEUKFINALMILE" to "Dine UK Final Mile"
    if (name === 'DINEUKFINALMILE') return 'Dine UK Final Mile';
    if (name === 'DINEUSA99') return 'Dine USA 99';
    
    // Generic formatting for other org names
    return name
      .toLowerCase()
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces before capitals
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  };

  // Extract organization permissions from user permissions
  const getOrganizationPermissions = (): Organization[] => {
    const orgPermissions: { [key: string]: Set<string> } = {};
    
    userPermissions.forEach(permission => {
      if (permission.startsWith('organizations_')) {
        const parts = permission.split('_');
        if (parts.length >= 3) {
          const orgId = parts[1];
          const permType = parts[2]; // 'read', 'write', or 'admin'
          
          if (!orgPermissions[orgId]) {
            orgPermissions[orgId] = new Set();
          }
          orgPermissions[orgId].add(permType);
        }
      }
    });

    return Object.entries(orgPermissions).map(([id, perms]) => {
      const orgConfig = getOrganizationById(id);
      const orgName = getOrganizationName(id);
      
      return {
        id,
        name: formatOrganizationName(orgName), // UPDATED: Use formatted organization name
        locationName: orgConfig?.locationName || 'Unknown Location', // Add location info
        permissions: Array.from(perms)
      };
    });
  };

  const organizations = getOrganizationPermissions();

  // If user has access to only one organization, auto-select it
  React.useEffect(() => {
    if (organizations.length === 1 && !locationsLoading) {
      onSelectOrganization(organizations[0].id);
    }
  }, [organizations, onSelectOrganization, locationsLoading]);

  // If only one organization, don't render the selector
  if (organizations.length <= 1) {
    return null;
  }

  // Show loading state if locations are still loading
  if (locationsLoading) {
    return (
      <div className="orgselect-container">
        <div className="orgselect-header">
          <h2 className="orgselect-title">Loading Organizations...</h2>
          <p className="orgselect-subtitle">Please wait while we load your organization data</p>
        </div>
        <div className="orgselect-loading">
          <div className="orgselect-loading-spinner"></div>
        </div>
      </div>
    );
  }

  const getPermissionBadges = (permissions: string[]) => {
    return permissions.map(perm => (
      <span 
        key={perm} 
        className={`orgselect-permission-badge orgselect-${perm}`}
      >
        {perm}
      </span>
    ));
  };

  const getHighestPermission = (permissions: string[]): string => {
    if (permissions.includes('admin')) return 'admin';
    if (permissions.includes('write')) return 'write';
    return 'read';
  };

  return (
    <div className="orgselect-container">
      <div className="orgselect-header">
        <h2 className="orgselect-title">Select Organization</h2>
        <p className="orgselect-subtitle">Choose an organization to manage its apps</p>
      </div>
      
      {organizations.length > 0 ? (
        <div className="orgselect-cards">
          {organizations.map(org => (
            <div
              key={org.id}
              className={`orgselect-card orgselect-${getHighestPermission(org.permissions)}`}
              onClick={() => onSelectOrganization(org.id)}
            >
              <div className="orgselect-card-content">
                <div className="orgselect-org-info">
                  <h3 className="orgselect-card-title">{org.name}</h3> {/* UPDATED: Show formatted name */}
                  <div className="orgselect-org-location">{org.locationName}</div> {/* ADD: Show location */}
                  <div className="orgselect-org-id">ID: {org.id}</div> {/* Keep ID as secondary info */}
                  <div className="orgselect-permissions">
                    {getPermissionBadges(org.permissions)}
                  </div>
                </div>
              </div>
              <div className="orgselect-card-actions">
                <button 
                  type="button"
                  className="orgselect-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectOrganization(org.id);
                  }}
                >
                  Manage Apps
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="orgselect-empty-state">
          <h3>No Organizations Available</h3>
          <p>You don't have access to any organizations. Please contact an administrator.</p>
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;